import React from 'react'
import { Icon } from '@iconify/react'
import { FormikProps } from 'formik'

import {
  DocBriefDetailsModal,
  useGetBriefs,
  useGetBriefContent,
  useUtils,
  Button,
} from '@pepper/ui'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { ReactComponent as EmptyIcon } from 'assets/images/template-empty-state.svg'

import styles from './_.module.css'

interface Props {
  actions: FormikProps<unknown>
  projectCategoryId?: string
  selectedBriefId?: string
  setSelectedBriefId?: (val: string) => void
  disableBriefSelection?: boolean
  showBriefModal: boolean
  setShowBriefModal: (val: boolean) => void
}

const DocsBriefComponent: React.FC<Props> = ({
  actions,
  projectCategoryId,
  selectedBriefId,
  setSelectedBriefId,
  disableBriefSelection = false,
  showBriefModal,
  setShowBriefModal,
}) => {
  const companyId = useSelectedCompanyId()
  const { project_categories } = useUtils()

  const briefs = useGetBriefs({ data: { companyId, include_drafts: true } })
  const chosenBrief = briefs.data?.filter(
    data => data.id === selectedBriefId
  )[0]
  const briefContent = useGetBriefContent({
    data: {
      pepperDocId: chosenBrief?.pepper_doc_id,
    },
  })

  const projectCategory = project_categories.find(
    category => category.id === Number.parseInt(projectCategoryId ?? 'NaN')
  )

  const openBrief = (briefId?: string, pepperDocId?: string) => {
    if (!briefId) return
    window.open(`/${companyId}/docs/${pepperDocId}?context_id=${briefId}`)
  }

  const handleSetBrief = (briefId: string | null) => {
    setSelectedBriefId && briefId && setSelectedBriefId(String(briefId))
    actions.setFieldValue('document_brief_id', briefId)
  }

  if (!chosenBrief) {
    return (
      <div className={styles.briefComponentContainer}>
        {showBriefModal && (
          <DocBriefDetailsModal
            platform="business"
            showDocBriefModal={showBriefModal}
            setShowBriefModal={setShowBriefModal}
            briefs={briefs?.data}
            setSelectedBriefId={handleSetBrief}
            companyId={companyId}
            projectCategory={projectCategory?.key}
          />
        )}
        <div className={styles.emptyBriefComponent}>
          <div className={styles.emptyIcon}>
            <EmptyIcon />
          </div>
          <div className={styles.emptyBriefHeader}>Add Creative Brief</div>
          <div className={styles.emptyBriefText}>
            Add a creative to add more details to your project. You can <br />{' '}
            create a new brief document or choose an existing one.
          </div>
          <div className={styles.buttonContainer}>
            <Button
              type="primary"
              onClick={() => setShowBriefModal(true)}
              disabled={disableBriefSelection}
            >
              Add a Creative Brief
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    (chosenBrief || selectedBriefId) && (
      <>
        {showBriefModal && (
          <DocBriefDetailsModal
            platform="business"
            showDocBriefModal={showBriefModal}
            setShowBriefModal={setShowBriefModal}
            briefs={briefs?.data}
            setSelectedBriefId={handleSetBrief}
            companyId={companyId}
          />
        )}
        <div className={styles.briefTextContainer}>
          <div className={styles.briefHeader}>
            <div className={styles.headerText}>
              <Icon width={12} icon="lucide:file-text" />
              <div className={styles.briefTitle}>{`${chosenBrief.title}`}</div>
              <span className={styles.line} />
              <div className={styles.briefCreator}>
                Created by{' '}
                {chosenBrief &&
                  `${chosenBrief.creator.first_name} ${chosenBrief.creator.last_name}`}
              </div>
            </div>
            <div
              className={styles.extLink}
              role="none"
              onClick={() => {
                openBrief(selectedBriefId, chosenBrief?.pepper_doc_id)
              }}
            >
              <Icon icon="tabler:external-link" className={styles.extIcon} />{' '}
              Open in docs
            </div>
          </div>
          <div className={styles.briefTextDisplay}>
            <div
              dangerouslySetInnerHTML={{
                __html: briefContent.data?.content.html ?? '',
              }}
              className={styles.briefHTML}
            />
          </div>
        </div>
      </>
    )
  )
}

export default DocsBriefComponent
